// Angular-Module
import {Injectable} from '@angular/core';
// ReactiveX for JavaScript
import {Observable} from 'rxjs';
// Globale Services
import {AppCoreService} from '@global/services/app-core.service';
import {BackendService} from '@global/services/backend.service';
// Interfaces für Structured Objects einbinden
import {CWEvent} from '@shared/cw-event';
import {EventData} from '@shared/event-data';
// Services von Shared-Modules
import {TranslateService} from '@ngx-translate/core';
import {CWResult} from '@shared/cw-result';
import {EventsPermissionsService} from '@shared/events/events-permissions.service';
import {InputDateService} from '@shared/input/input-date/input-date.service';

@Injectable({providedIn: 'root'})
export class EventsDataService {
    /**
     * Konstruktor (inkl. dependency injection)
     * @param appCore
     * @param backendService
     * @param inputDateService
     * @param eventsPermissionsService
     * @param translateService
     */
    constructor(
        private appCore: AppCoreService,
        private backendService: BackendService,
        private inputDateService: InputDateService,
        private eventsPermissionsService: EventsPermissionsService,
        private translateService: TranslateService,
    ) {}

    /**
     * @param id
     * @brief   Stammdaten einer Veranstaltung laden
     * @returns  Observable<any>
     */
    loadData(id: number): Observable<any> {
        // GET-Request über BackendService senden
        const getRequest$: Observable<any> = this.backendService.getRequest('EventsData/details/' + id);
        // Observable (an Komponente) zurücklieferen
        return getRequest$;
    }

    /**
     * @param id
     * @brief   Stammdaten einer Veranstaltung löschen
     * @returns  Observable<any>
     */
    deleteData(id: number): Observable<any> {
        // GET-Request über BackendService senden
        const getRequest$: Observable<any> = this.backendService.getRequest('EventsData/delete/' + id);
        // Observable (an Komponente) zurücklieferen
        return getRequest$;
    }

    /**
     * @param id
     * @brief   Teilnehmer-Daten einer Veranstaltung laden
     * @returns  Observable<any>
     */
    loadParticipantsData(id: number): Observable<any> {
        // GET-Request über BackendService senden
        const getRequest$: Observable<any> = this.backendService.getRequest('EventsData/getParticipantsData/' + id);
        // Observable (an Komponente) zurücklieferen
        return getRequest$;
    }

    /**
     * @param id
     * @param formData
     * @brief   Speichert Stammdaten einer Veranstaltung
     * @returns  Observable<any>
     */
    saveData(id: number, formData: any): Observable<any> {
        if (typeof formData.event_start !== 'undefined') {
            formData['event_start'] = this.inputDateService.getDateValueForSave(formData.event_start);
        }
        if (typeof formData.event_end !== 'undefined') {
            formData['event_end'] = this.inputDateService.getDateValueForSave(formData.event_end);
        }
        // POST-Request über BackendService senden
        const postRequest$: Observable<any> = this.backendService.postRequest('EventsData/edit/' + id, formData);
        // Observable (an Komponente) zurücklieferen
        return postRequest$;
    }

    /**
     * @param id
     * @param formData
     * @brief   Speichert Stammdaten einer Veranstaltung
     * @returns  Observable<any>
     */
    saveDataInstitutions(id: number, formData: any): Observable<any> {
        if (typeof formData.event_start !== 'undefined') {
            formData['event_start'] = this.inputDateService.getDateValueForSave(formData.event_start);
        }
        if (typeof formData.event_end !== 'undefined') {
            formData['event_end'] = this.inputDateService.getDateValueForSave(formData.event_end);
        }
        // POST-Request über BackendService senden
        const postRequest$: Observable<any> = this.backendService.postRequest('EventsData/createFromInstitution/', formData);
        // Observable (an Komponente) zurücklieferen
        return postRequest$;
    }

    /**
     * @param data
     * @param sender
     * @brief      Falls Daten erfolgreich geändert wurden
     * @details    Sendet aktualisierten Datensatz per Event, damit andere
     *             Module (z.B. Listen) darauf reagieren können
     */
    dataChanged(data: EventData, sender = 'events-data'): void {
        // Informationen für Event
        const eventData: CWEvent = {
            sender,
            target: '',
            data: {changedItem: data},
        };
        // Event auslösen
        this.appCore.appDataChanged.next(eventData);
        // Permissions aktualisieren lassen
        this.eventsPermissionsService.selectionChanged(data.id);
    }

    /**
     * @param id
     * @brief   Mögliche Statuswerte der Veranstaltung laden
     * @returns  Observable<CWResult<SelectData>>
     * @author Lennart Bentz <l.bentz@pharmakon.software>
     */
    loadStatusSelectData(id: number): Observable<CWResult> {
        const languageKey = this.translateService.currentLang;
        // GET-Request über BackendService senden, Observable zurückgeben
        return this.backendService.getRequest(
            'EventsData/getEventStatusSelectData/' + languageKey + '/' + id,
        ) as Observable<CWResult>;
    }
}
